<script setup>
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

// «ما يستطيع فعله وحدوده»: the plain-words contract of the manager advisor,
// shown on demand above the chat so the manager knows, before asking, what
// is read, what runs after approval, what is refused, and what is still
// only explained. The lists mirror the closed catalogue in
// custom/app/services/teletel/advisor/actions.rb and lookups.rb; when that
// catalogue grows, these strings grow with it (owner order 2026-09-22).
defineEmits(['close']);

const { t, tm, rt } = useI18n();
// vue-i18n returns raw messages from tm(); rt() resolves each string.
const list = key => {
  const items = tm(`TELETEL_ADVISOR.CAPABILITIES.${key}`);
  return Array.isArray(items) ? items : [];
};
const text = item => rt(item);
</script>

<template>
  <section
    class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-5 py-4 text-sm"
    :aria-label="t('TELETEL_ADVISOR.CAPABILITIES.TITLE')"
  >
    <div class="flex items-start gap-3">
      <span
        class="i-lucide-shield-check mt-0.5 size-5 shrink-0 text-n-blue-11"
      />
      <div class="flex flex-1 flex-col gap-1">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('TELETEL_ADVISOR.CAPABILITIES.TITLE') }}
        </h2>
        <p class="text-n-slate-11">
          {{ t('TELETEL_ADVISOR.CAPABILITIES.LEAD') }}
        </p>
      </div>
      <NextButton
        slate
        faded
        xs
        icon="i-lucide-x"
        :label="t('TELETEL_ADVISOR.CAPABILITIES.CLOSE')"
        @click="$emit('close')"
      />
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div class="rounded-lg border border-n-weak bg-n-solid-2 p-3">
        <h3 class="mb-2 flex items-center gap-1.5 font-medium text-n-slate-12">
          <span class="i-lucide-eye size-4 text-n-slate-11" />
          {{ t('TELETEL_ADVISOR.CAPABILITIES.READS_TITLE') }}
        </h3>
        <ul class="flex list-disc flex-col gap-1 ps-4 text-n-slate-11">
          <li v-for="(item, i) in list('READS')" :key="`r${i}`">
            {{ text(item) }}
          </li>
        </ul>
      </div>

      <div class="rounded-lg border border-n-teal-6 bg-n-teal-2 p-3">
        <h3 class="mb-2 flex items-center gap-1.5 font-medium text-n-teal-12">
          <span class="i-lucide-circle-check size-4" />
          {{ t('TELETEL_ADVISOR.CAPABILITIES.ACTIONS_TITLE') }}
        </h3>
        <ul class="flex list-disc flex-col gap-1 ps-4 text-n-teal-12">
          <li v-for="(item, i) in list('ACTIONS')" :key="`a${i}`">
            {{ text(item) }}
          </li>
        </ul>
      </div>

      <div class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-3">
        <h3 class="mb-2 flex items-center gap-1.5 font-medium text-n-ruby-12">
          <span class="i-lucide-circle-x size-4" />
          {{ t('TELETEL_ADVISOR.CAPABILITIES.LIMITS_TITLE') }}
        </h3>
        <ul class="flex list-disc flex-col gap-1 ps-4 text-n-ruby-12">
          <li v-for="(item, i) in list('LIMITS')" :key="`l${i}`">
            {{ text(item) }}
          </li>
        </ul>
      </div>
    </div>

    <div
      class="rounded-lg border border-n-amber-6 bg-n-amber-2 p-3 text-n-amber-12"
    >
      <h3 class="mb-2 flex items-center gap-1.5 font-medium">
        <span class="i-lucide-shield-alert size-4" />
        {{ t('TELETEL_ADVISOR.CAPABILITIES.CONFIRM_TITLE') }}
      </h3>
      <ul class="flex list-disc flex-col gap-1 ps-4">
        <li v-for="(item, i) in list('CONFIRM')" :key="`c${i}`">
          {{ text(item) }}
        </li>
      </ul>
    </div>

    <div class="rounded-lg border border-n-weak bg-n-solid-2 p-3">
      <h3 class="mb-2 flex items-center gap-1.5 font-medium text-n-slate-12">
        <span class="i-lucide-sparkles size-4 text-n-slate-11" />
        {{ t('TELETEL_ADVISOR.CAPABILITIES.EXAMPLES_TITLE') }}
      </h3>
      <dl class="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-[1fr_1fr]">
        <template v-for="(ex, i) in list('EXAMPLES')" :key="`e${i}`">
          <dt class="flex items-start gap-1.5 text-n-slate-12">
            <span class="i-lucide-user mt-1 size-3 shrink-0 text-n-blue-11" />
            {{ text(ex.ASK) }}
          </dt>
          <dd class="flex items-start gap-1.5 text-n-slate-11">
            <span
              class="i-lucide-arrow-left rtl:rotate-180 mt-1 size-3 shrink-0"
            />
            {{ text(ex.RESULT) }}
          </dd>
        </template>
      </dl>
    </div>

    <div
      class="rounded-lg border border-n-weak bg-n-solid-2 p-3 text-n-slate-12"
    >
      <h3 class="mb-1 flex items-center gap-1.5 font-medium">
        <span class="i-lucide-hand size-4 text-n-slate-11" />
        {{ t('TELETEL_ADVISOR.CAPABILITIES.NOT_YET_TITLE') }}
      </h3>
      <p>{{ t('TELETEL_ADVISOR.CAPABILITIES.NOT_YET') }}</p>
    </div>

    <p class="flex items-center gap-1.5 text-xs text-n-slate-10">
      <span class="i-lucide-history size-3.5" />
      {{ t('TELETEL_ADVISOR.CAPABILITIES.AUDIT_NOTE') }}
    </p>
  </section>
</template>
